import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { WBSButton } from '@/common/WBSButton';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { FollowUpStudyInfo } from '@/follow-up/followUpStudyInfo';
import {
  SectionHeading,
  ViewBody,
  ViewContainer,
  ViewHeader,
  ViewHeaderTitle,
} from '@/follow-up/followUpStyles';
import { PendingRequestContactInfo } from '@/follow-up/PendingRequestContactInfo';
import { StudentLocationNotice } from '@/follow-up/StudentLocationNotice';
import { AvatarNameCell } from '@/tables/AvatarNameCell';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { formatLongDate } from '~/utils/dateFormat';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute(
  '/_app/follow-ups/pending-requests/view/$followUpId',
)({
  component: PartnerPendingFollowUpScreen,
});

/**
 * The partner's accept/reject view —
 * `groups/single/follow-up-partner-group/follow-ups/pending-requests/view.php`
 * (158 lines).
 *
 * NOT the shared detail, and not built by copying it. Its path variable is a
 * FOLLOW-UP id, not a request id (`:2`), and the coordinator's twin of this
 * file is a two-line debug stub (`<?php echo __FILE__;`), which is why the
 * whole route is partner-only.
 *
 * The two dates on this page are DIFFERENT COLUMNS wearing the same label.
 * *Requested On* here is `wbs_date($follow_post->post_date)` (`:80`) — the
 * FOLLOW-UP's own date, which `FollowUpDetail` publishes as
 * `partner_assigned_at`, not the `requested_on` field, which is the REQUEST's.
 * Over the eleven rows this queue holds the two differ by between 2d23h and
 * 551 days, so binding the line to the field whose name matches the label is a
 * date up to a year and a half wrong.
 */
function PartnerPendingFollowUpScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { followUpId } = Route.useParams();
  const id = Number(followUpId);
  const { me, isPending: mePending } = useCurrentUser();

  const [rejectReason, setRejectReason] = useState('');

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getFollowUp.useQuery({ path: { follow_up_id: id } });

  const {
    mutateAsync: accept,
    isPending: accepting,
    error: acceptError,
  } = api.acceptFollowUp.useMutation();
  const {
    mutateAsync: reject,
    isPending: rejecting,
    error: rejectError,
  } = api.rejectFollowUp.useMutation();

  const backToQueue = () =>
    void navigate({ to: '/follow-ups/pending-requests/' });

  const afterDecision = async (): Promise<void> => {
    await Promise.all([
      api.listPartnerPendingFollowUps.invalidateQueries(),
      // Accept moves the row onto the partner's entries tab; reject does not,
      // but invalidating both is one refetch either way.
      api.listPartnerFollowUps.invalidateQueries(),
      api.getFollowUp.invalidateQueries(),
    ]);
    // `redirect_to` at `:142` is the queue this row was opened from.
    backToQueue();
  };

  const handleAccept = async (): Promise<void> => {
    try {
      await accept({ path: { follow_up_id: id } });
    } catch {
      return;
    }
    await afterDecision();
  };

  const handleReject = async (): Promise<void> => {
    try {
      await reject({
        path: { follow_up_id: id },
        body: { message_for_coordinator: rejectReason },
      });
    } catch {
      return;
    }
    await afterDecision();
  };

  // Defence in depth only — `get_follow_up` already answers 404 for anyone
  // outside the row's viewer set, and only the invited partner gets
  // `can_accept_or_reject`. ADMIN is deliberately absent (DIV-2): this is the
  // partner's own queue and an admin has no row in `followup_partners`.
  if (mePending || isLoading) return null;
  if (me?.profile_type !== 'FOLLOWUP_PARTNER') return <NotFoundScreen />;
  if (isError || detail === undefined) return <NotFoundScreen />;

  const writeError = acceptError ?? rejectError;

  return (
    <PageContainer>
      <ViewContainer>
        <LargeArrowBackButton onClick={backToQueue} />
        <ViewHeader>
          <ViewHeaderTitle>
            {formatString(t('wbs.follow-up-s'), String(detail.id))}
          </ViewHeaderTitle>
        </ViewHeader>
        <ViewBody>
          <Columns>
            <Column>
              <Section>
                <SectionHeading>
                  {t('lessons.student-s-contact-info')}
                </SectionHeading>
                <AvatarNameCell
                  avatar={detail.student?.avatar ?? null}
                  name={detail.student?.display_name ?? ''}
                  linkUserId={detail.student?.id}
                />
                {/* `:46-49` includes the shared `student-contact-info.php` over
                    the FOLLOW-UP's stored snapshot (R12). */}
                <PendingRequestContactInfo
                  contact={detail.student_contact_info}
                />
              </Section>
              <Section>
                <SectionHeading>
                  {t('lessons.student-s-study-info')}
                </SectionHeading>
                {/* DIV-25 — `student-study-info.php` decodes the snapshot on
                    line 1 and OVERWRITES it with
                    `wbs_get_user_study_progress($student_id)` on line 2, so the
                    four rows here are live. The endpoint resolves them. */}
                <FollowUpStudyInfo
                  progress={detail.student_study_progress}
                  progressLabel={detail.study_progress_label}
                />
              </Section>
            </Column>

            <Column>
              <Section>
                <SectionHeading>{t('wbs.requested-by')}</SectionHeading>
                <AvatarNameCell
                  avatar={detail.coordinator?.avatar ?? null}
                  name={detail.coordinator?.display_name ?? ''}
                  linkUserId={detail.coordinator?.id}
                />
              </Section>
              <Section>
                <SectionHeading>{t('wbs.requested-on')}</SectionHeading>
                {/* The FOLLOW-UP's own date — see the docstring. */}
                <div>{formatLongDate(detail.partner_assigned_at)}</div>
              </Section>
              <Section>
                <SectionHeading>{t('wbs.selected-requests')}</SectionHeading>
                <ObjectiveList>
                  {detail.selected_requests.map((option) => (
                    <li key={option.value}>{option.label}</li>
                  ))}
                </ObjectiveList>
              </Section>
              {/* `:101` falls back to `__('No message', 'wbs')` — unit 22733,
                  NO period. `wbs.no-message` is unit 22708, `'No message.'`,
                  a different unit with different ES/FR/PT_BR text. */}
              <FollowUpNotes
                title={t('wbs.follow-up-coordinator-s-message')}
                message={detail.message_for_partner || t('wbs.no-message-2')}
                $smallTitle
              />
            </Column>
          </Columns>

          {/* §10.3 — the map is not built, so the notice legacy shows only on a
              geocoding failure renders unconditionally. `view.php:111` passes
              no address, unlike convert's. */}
          <StudentLocationNotice />

          {writeError != null && (
            <AlertBox variant="error">
              {errorMessage(
                writeError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
          )}

          {/* `:134-139` — the textarea renders whatever the status is; only the
              two buttons are gated. */}
          <Section>
            <SectionHeading>
              {t('wbs.message-for-follow-up-coordinator')}
            </SectionHeading>
            <RejectReason
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              rows={4}
              data-testid="follow-up-reject-reason"
            />
          </Section>

          {/* `:145` — Reject and Accept only while `partner_status == 'pending'`.
              `can_accept_or_reject` is exactly that condition ANDed with "the
              caller is the invited partner", so a partner reaching an
              already-decided row sees the page with no buttons and the 409 is
              a second line of defence rather than the gate. */}
          {detail.can_accept_or_reject && (
            <Actions>
              <WBSButton
                alternate
                type="button"
                onClick={() => void handleReject()}
                isLoading={rejecting}
                data-testid="reject-follow-up"
              >
                {t('wbs.reject')}
              </WBSButton>
              <WBSButton
                type="button"
                onClick={() => void handleAccept()}
                isLoading={accepting}
                data-testid="accept-follow-up"
              >
                {t('wbs.accept')}
              </WBSButton>
            </Actions>
          )}
        </ViewBody>
      </ViewContainer>
    </PageContainer>
  );
}

const Columns = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ObjectiveList = styled.ul`
  font-size: 14px;
  margin: 0;
  padding-left: 20px;
`;

const RejectReason = styled.textarea`
  background: var(--grey-100);
  border: none;
  border-radius: 6px;
  box-shadow: var(--shadow-inset-md);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;
